<script setup lang="ts">
import { Form } from "@inertiajs/vue3"
import type { AcceptableValue } from "reka-ui"
import { ref } from "vue"

import DatePicker from "@/components/custom/DatePicker.vue"
import FileInput from "@/components/custom/FileInput.vue"
import FormField from "@/components/custom/FormField.vue"
import { Alert } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { studyPath } from "@/routes"

defineProps<{
  disabled?: boolean
}>()
defineEmits<{
  success: []
  cancel: []
}>()

const showTrainingTypeInput = ref(false)
// const today = new Date()
// const maxDate = new Date(
//   today.getFullYear(),
//   today.getMonth(),
//   today.getDate() - 1,
// )

const startDate = ref<Date | undefined>()
const endDate = ref<Date | undefined>()

function onStartDateChange(val: Date | string | null | undefined) {
  if (!val) {
    startDate.value = undefined
    return
  }

  startDate.value = val instanceof Date ? val : new Date(val)

  if (endDate.value && startDate.value && endDate.value < startDate.value) {
    endDate.value = undefined
  }
}

function toggleTrainingType(value: AcceptableValue) {
  showTrainingTypeInput.value = value === ""
}
</script>

<template>
  <Transition>
    <Card>
      <CardHeader>
        <CardTitle>Nueva Solicitud de Ayuda de Estudios</CardTitle>
        <CardDescription>
          Completa todos los campos y adjunta los documentos requeridos
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form
          class="flex flex-col"
          :action="studyPath()"
          method="POST"
          :options="{
            preserveScroll: true,
            preserveState: true,
          }"
          autocomplete="off"
          @success="$emit('success')"
          #default="{ processing, errors }"
        >
          <fieldset
            class="grid grid-cols-[190px_1fr_1fr] gap-10"
            :disabled="disabled"
          >
            <div class="flex flex-col gap-3">
              <FormField
                label="Tipo de formación"
                required
                :errors="(!showTrainingTypeInput && errors.training_type) || []"
              >
                <RadioGroup
                  class="grid gap-3"
                  @update:model-value="toggleTrainingType"
                >
                  <Label class="cursor-pointer font-normal">
                    <RadioGroupItem
                      name="training_type"
                      value="Formación técnica"
                    />
                    Formación técnica
                  </Label>
                  <Label class="cursor-pointer font-normal">
                    <RadioGroupItem name="training_type" value="Idiomas" />
                    Idiomas
                  </Label>
                  <Label class="cursor-pointer font-normal">
                    <RadioGroupItem name="training_type" value="" />
                    Otra (especifica)
                  </Label>
                </RadioGroup>
              </FormField>

              <FormField
                v-if="showTrainingTypeInput"
                label="Especifica el tipo de formación"
                required
                :errors="errors.training_type"
              >
                <Input
                  placeholder="Especifica el tipo de formación"
                  name="training_type"
                />
              </FormField>
            </div>

            <div class="space-y-3">
              <FormField
                label="Nombre de la formación"
                required
                :errors="errors.course_name"
              >
                <Input
                  placeholder="Ej: Inglés B2 Cambridge"
                  name="course_name"
                />
              </FormField>
              <FormField
                label="Entidad que imparte la formación"
                required
                :errors="errors.provider"
              >
                <Input
                  placeholder="Ej: Oxford School Barcelona"
                  name="provider"
                />
              </FormField>

              <div class="grid grid-cols-2 gap-3">
                <FormField
                  label="Fecha de inicio"
                  required
                  :errors="errors.start_date"
                >
                  <DatePicker
                    name="start_date"
                    v-model="startDate"
                    @update:model-value="onStartDateChange"
                  />
                </FormField>
                <FormField
                  label="Fecha de finalización"
                  required
                  :errors="errors.end_date"
                >
                  <DatePicker
                    name="end_date"
                    v-model="endDate"
                    :min-date="startDate"
                  />
                </FormField>
              </div>

              <div>
                <FormField
                  label="Importe total formación"
                  required
                  :errors="errors.total_amount"
                >
                  <InputGroup>
                    <InputGroupInput
                      name="total_amount"
                      placeholder="0.00"
                      type="number"
                    />
                    <InputGroupAddon align="inline-end"> € </InputGroupAddon>
                  </InputGroup>
                </FormField>

                <!--<Alert class="mt-4">
                  <p class="text-xs">
                    <strong>Subvención:</strong> La empresa subvencionará hasta
                    un máximo de <strong>218.10€ brutos</strong>. Si tu
                    formación cuesta menos, recibirás el importe que te haya
                    costado la formación. Si cuesta más de 218.10€, recibirás
                    218.10€.
                  </p>
                </Alert>-->
              </div>
            </div>

            <div class="flex flex-col gap-3">
              <Label class="font-medium">Documentos (Obligatorios)</Label>

              <Alert class="border-2 border-blue-300">
                <div class="flex items-start gap-2">
                  <div>
                    <p class="mt-1 text-xs">
                      Debes adjuntar un
                      <strong
                        >screenshot del email donde tu manager aprueba que hagas
                        esta formación</strong
                      >. Este documento es obligatorio para todas las
                      solicitudes de ayuda de estudios.
                    </p>
                  </div>
                </div>
              </Alert>

              <FormField
                name="syllabus"
                type="file"
                label="1. Programa de contenidos"
                :errors="errors.syllabus"
                required
              >
                <FileInput name="syllabus" accept="image/*,application/pdf" />
              </FormField>
              <FormField
                name="payment_receipt"
                type="file"
                label="2. Comprobante de pago"
                :errors="errors.payment_receipt"
                required
              >
                <FileInput
                  name="payment_receipt"
                  accept="image/*,application/pdf"
                />
              </FormField>
              <FormField
                name="manager_approval"
                type="file"
                label="3. Screenshot del email de aprobación del manager"
                :errors="errors.manager_approval"
                required
              >
                <FileInput
                  name="manager_approval"
                  accept="image/*,application/pdf"
                />
              </FormField>
              <FormField
                name="completion_certificate"
                type="file"
                label="4. Certificado de Superación de Formación 2025"
                :errors="errors.completion_certificate"
              >
                <FileInput
                  name="completion_certificate"
                  accept="image/*,application/pdf"
                />
              </FormField>

              <p class="text-xs text-gray-500">
                0/3 documentos subidos (mínimo 3 obligatorios)
              </p>
            </div>
          </fieldset>

          <div class="mt-10 flex w-75 gap-3 self-end">
            <Button
              type="submit"
              class="flex-1"
              :disabled="disabled || processing"
            >
              {{ processing ? "Enviando..." : "Enviar Solicitud" }}
            </Button>
            <Button
              type="button"
              variant="outline"
              class="flex-1 cursor-pointer"
              @click="$emit('cancel')"
            >
              Limpiar
            </Button>
          </div>
        </Form>
      </CardContent>
    </Card>
  </Transition>
</template>
